import { Component, computed, inject, input, output } from '@angular/core';
import { MatBadge } from '@angular/material/badge';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { UsersService } from '../../../../users/service/users.service';
import { Cell } from '../interfaces/cell.interface';

@Component({
  standalone: true,
  imports: [MatBadge, MatIcon, MatTooltip],
  selector: 'app-analysis-overview-cell-clickable',
  template: `
    <div
      [class.active]="active()"
      (click)="click.emit($event)"
      [matBadge]="badgeContent()"
      [style.--mat-badge-background-color]="badgeBgColour()"
      [style.--mat-badge-text-color]="badgeTxtColour()"
      [matTooltip]="cell()?.measurement?.comment"
    >
      @if (hasMissingSavedCategory()) {
        <mat-icon
          aria-label="Missing factor applied category"
          fontIcon="warning_amber"
          matTooltip="Factor applied category is not available for this measurement"
        />
      } @else {
        {{ cell()?.abundance }}
      }
    </div>
  `,
  styles: `
    div {
      height: 48px;
      width: 48px;

      display: flex;
      justify-content: center;
      align-items: center;

      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;

      color: #000;

      border-bottom: 1px solid var(--border-color);
      border-right: 1px solid var(--border-color);

      cursor: pointer;
    }

    .active {
      background: rgba(206, 232, 229, 0.3);
    }

    .mat-badge-content {
      left: calc(100% - 6px);
      bottom: calc(100% - 6px);
    }
  `,
})
export class AnalysisOverviewClickableCellComponent {
  private readonly usersService = inject(UsersService);

  // Inputs

  readonly cell = input<Cell>(null);
  readonly active = input<boolean>();

  // Outputs

  readonly click = output<PointerEvent>();

  // Computed States

  readonly hasMissingSavedCategory = computed(() => {
    const measurement = this.cell()?.measurement;

    return (
      !!measurement &&
      measurement.analysedCount !== '+' &&
      measurement.analysedCount !== undefined &&
      this.cell()?.abundance === undefined
    );
  });

  readonly cbResolved = computed(
    () => !!this.cell()?.checkBlock?.resolved && !!this.cell()?.checkBlock?.result,
  );

  readonly badgeContent = computed(() => {
    if (this.cell()?.checkBlock) {
      return this.cbResolved() ? '✓' : '!';
    }
    // else return void (no content)
  });

  // Yellow : Raised, senior last message
  // Blue   : assigned analyst last message
  // Red    : Unassigned
  // Green  : resolved
  readonly badgeBgColour = computed(() => {
    const cb = this.cell().checkBlock;
    const resolved = this.cbResolved();
    return (() => {
      if (!cb) {
        return;
      }

      if (!cb.userId) {
        return 'indianred';
      }

      if (resolved) {
        return 'var(--primary-color)';
      }

      if (cb.messages.at(-1).userId === cb.userId) {
        return 'blue';
      }

      return 'yellow';
    })();
  });

  // Make badge txt black if bg is yellow
  readonly badgeTxtColour = computed(() => (this.badgeBgColour() === 'yellow' ? 'black' : 'white'));
}
